/**
 * Composant Navbar réutilisable
 * 
 * @example
 * // Utilisation basique
 * import { Navbar } from '@/components/Navbar';
 * 
 * function MaPage() {
 *   return (
 *     <div>
 *       <Navbar />
 *       // ... contenu de votre page
 *     </div>
 *   );
 * }
 * 
 * @example
 * // Avec personnalisation
 * <Navbar backgroundColor="#f3f4f6" showAnimations={false} />
 */

import React, { useEffect, useId, useMemo, useRef, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { trackEvent } from '../utils/gtag';
import { LanguageFlagSelector } from './LanguageFlagSelector';
import {
  ArrowRight,
  ChevronDown,
  Handshake,
  LogIn,
  Menu,
  MessageSquare,
  X,
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { authService } from '../services/authService';

// Images
import logoSvg from '../assets/focuspips.svg';

import logoSvgWebP from '../assets/webp-landing/focuspips.webp';
import logoSvgSmWebP from '../assets/webp-landing/focuspips-sm.webp';

// Composant FastImage pour optimisation
const FastImage = React.memo(({ src, srcWebP, srcSmWebP, alt, className, priority = false, width, height }: { src: string; srcWebP?: string; srcSmWebP?: string; alt: string; className: string; priority?: boolean; width?: number; height?: number }) => { 
  if (srcWebP) {
    return (
      <picture>
        {srcSmWebP && <source media="(max-width: 639px)" srcSet={srcSmWebP} type="image/webp" />}
        <source srcSet={srcWebP} type="image/webp" />
        <img 
          src={src} 
          alt={alt} 
          className={className} 
          loading={priority ? 'eager' : 'lazy'}
          decoding={priority ? 'sync' : 'async'}
          // @ts-ignore
          fetchpriority={priority ? 'high' : 'auto'}
          width={width}
          height={height}
        />
      </picture>
    );
  }
  return <img src={src} alt={alt} className={className} loading={priority ? 'eager' : 'lazy'} {...{fetchpriority: priority ? 'high' : 'auto'}} width={width} height={height} />;
});

FastImage.displayName = 'FastImage';

type LandingIconProps = {
  className?: string;
};

const FocusGradientDefs = ({ gradientId }: { gradientId: string }) => (
  <defs>
    <linearGradient id={gradientId} x1="3" y1="3" x2="21" y2="21" gradientUnits="userSpaceOnUse">
      <stop stopColor="#7C76FF" />
      <stop offset="0.52" stopColor="#F35BB9" />
      <stop offset="1" stopColor="#FFB36B" />
    </linearGradient>
  </defs>
);

const JournalMenuIcon = ({ className }: LandingIconProps) => {
  const gradientId = useId();

  return (
    <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" className={className}>
      <FocusGradientDefs gradientId={gradientId} />
      <path d="M3 8C3 5.17157 3 3.75736 3.87868 2.87868C4.75736 2 6.17157 2 9 2H15C17.8284 2 19.2426 2 20.1213 2.87868C21 3.75736 21 5.17157 21 8V16C21 18.8284 21 20.2426 20.1213 21.1213C19.2426 22 17.8284 22 15 22H9C6.17157 22 4.75736 22 3.87868 21.1213C3 20.2426 3 18.8284 3 16V8Z" stroke={`url(#${gradientId})`} strokeWidth="1.5"></path>
      <path d="M8 2.5V22" stroke={`url(#${gradientId})`} strokeWidth="1.5" strokeLinecap="round"></path>
      <path d="M2 12H4" stroke={`url(#${gradientId})`} strokeWidth="1.5" strokeLinecap="round"></path>
      <path d="M2 16H4" stroke={`url(#${gradientId})`} strokeWidth="1.5" strokeLinecap="round"></path>
      <path d="M2 8H4" stroke={`url(#${gradientId})`} strokeWidth="1.5" strokeLinecap="round"></path>
      <path d="M11.5 6.5H16.5" stroke={`url(#${gradientId})`} strokeWidth="1.5" strokeLinecap="round"></path>
      <path d="M11.5 10H16.5" stroke={`url(#${gradientId})`} strokeWidth="1.5" strokeLinecap="round"></path>
    </svg>
  );
};

const BacktestMenuIcon = ({ className }: LandingIconProps) => {
  const gradientId = useId();

  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" className={className}>
      <FocusGradientDefs gradientId={gradientId} />
      <g transform="matrix(0.700756 0 0 0.779158 8.19638 -17.126)">
        <path clipRule="evenodd" d="m13.69,23.89c0.21,0.18 0.21,0.47 0,0.64l-2.65,2.26l5.84,0c0.68,0 1.69,0.17 2.54,0.69c0.88,0.54 1.56,1.44 1.56,2.8c0,0.25 -0.24,0.46 -0.53,0.46c-0.29,0 -0.53,-0.21 -0.53,-0.46c0,-1.07 -0.51,-1.69 -1.11,-2.06c-0.63,-0.39 -1.41,-0.52 -1.92,-0.52l-5.84,0l2.65,2.26c0.21,0.18 0.21,0.47 0,0.64c-0.21,0.18 -0.55,0.18 -0.76,0l-3.56,-3.04c-0.21,-0.18 -0.21,-0.47 0,-0.64l3.56,-3.04c0.21,-0.18 0.55,-0.18 0.76,0l-0.01,0.01z" fill={`url(#${gradientId})`} fillRule="evenodd"/>
      </g>
      <rect fill={`url(#${gradientId})`} height="10.73" rx="0.2" width="3.06" x="0.01" y="9.24"/>
      <rect fill={`url(#${gradientId})`} height="6.8" transform="matrix(1 0 0 1 0 0) matrix(7.1018 0 0 0.326515 -91.4601 4.95259)" width="0.08" x="13.84" y="16.4"/>
      <rect fill={`url(#${gradientId})`} height="4.96" rx="0.2" transform="matrix(1 0 0 1 0 0) matrix(3.97357 0 0 1.50334 -43.6922 -13.4894)" width="0.74" x="12.43" y="17.3"/>
      <rect fill={`url(#${gradientId})`} height="6.8" transform="matrix(7.1018 0 0 0.188149 -91.4601 4.98085)" width="0.08" x="14.59" y="15.16"/>
      <rect fill={`url(#${gradientId})`} height="4.96" rx="0.2" transform="matrix(4.12419 0 0 0.757708 -46.8979 -11.1854)" width="0.74" x="14.02" y="26.77"/>
      <rect fill={`url(#${gradientId})`} height="6.8" transform="matrix(7.1018 0 0 0.225074 -91.4601 16.8006)" width="0.08" x="15.26" y="15.3"/>
      <rect fill={`url(#${gradientId})`} height="4.96" rx="0.2" transform="matrix(1 0 0 1 0 0) matrix(4.12419 0 0 1.50043 -46.8979 -16.1244)" width="0.74" x="15.17" y="19.3"/>
      <rect fill={`url(#${gradientId})`} height="6.8" transform="matrix(7.1018 0 0 0.372617 -91.4601 14.4184)" width="0.08" x="16.01" y="15.84"/>
      <rect fill={`url(#${gradientId})`} height="12.16" rx="0.2" width="3.06" x="20.91" y="8.18"/>
      <rect fill={`url(#${gradientId})`} height="6.8" transform="matrix(7.1018 0 0 0.217272 -91.4601 16.3995)" width="0.08" x="13.84" y="16.4"/>
      <rect fill={`url(#${gradientId})`} height="6.8" transform="matrix(7.1018 0 0 1.49395 -91.4601 -9.78967)" width="0.08" x="14.59" y="15.15"/>
      <rect fill={`url(#${gradientId})`} height="6.8" transform="matrix(7.41099 0 0 2.34754 -85.6608 -123.745)" width="0.08" x="11.72" y="55.89"/>
    </svg>
  );
};

const PlatformMenuIcon = ({ className }: LandingIconProps) => {
  const gradientId = useId();

  return (
    <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" className={className}>
      <FocusGradientDefs gradientId={gradientId} />
      <path d="M6 10H10" stroke={`url(#${gradientId})`} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"></path>
      <path d="M20.8333 11H18.2308C16.4465 11 15 12.3431 15 14C15 15.6569 16.4465 17 18.2308 17H20.8333C20.9167 17 20.9583 17 20.9935 16.9979C21.5328 16.965 21.9623 16.5662 21.9977 16.0654C22 16.0327 22 15.994 22 15.9167V12.0833C22 12.006 22 11.9673 21.9977 11.9346C21.9623 11.4338 21.5328 11.035 20.9935 11.0021C20.9583 11 20.9167 11 20.8333 11Z" stroke={`url(#${gradientId})`} strokeWidth="1.5"></path>
      <path d="M20.965 11C20.8873 9.1277 20.6366 7.97975 19.8284 7.17157C18.6569 6 16.7712 6 13 6H10C6.22876 6 4.34315 6 3.17157 7.17157C2 8.34315 2 10.2288 2 14C2 17.7712 2 19.6569 3.17157 20.8284C4.34315 22 6.22876 22 10 22H13C16.7712 22 18.6569 22 19.8284 20.8284C20.6366 20.0203 20.8873 18.8723 20.965 17" stroke={`url(#${gradientId})`} strokeWidth="1.5"></path>
      <path d="M6 6L9.73549 3.52313C10.7874 2.82562 12.2126 2.82562 13.2645 3.52313L17 6" stroke={`url(#${gradientId})`} strokeWidth="1.5" strokeLinecap="round"></path>
      <path d="M17.9912 14H18.0002" stroke={`url(#${gradientId})`} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"></path>
    </svg>
  );
};

const PricingMenuIcon = ({ className }: LandingIconProps) => {
  const gradientId = useId();

  return (
    <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" className={className}>
      <FocusGradientDefs gradientId={gradientId} />
      <circle cx="12" cy="12" r="10" stroke={`url(#${gradientId})`} strokeWidth="1.5"></circle>
      <path d="M12 17V17.5V18" stroke={`url(#${gradientId})`} strokeWidth="1.5" strokeLinecap="round"></path>
      <path d="M12 6V6.5V7" stroke={`url(#${gradientId})`} strokeWidth="1.5" strokeLinecap="round"></path>
      <path d="M15 9.5C15 8.11929 13.6569 7 12 7C10.3431 7 9 8.11929 9 9.5C9 10.8807 10.3431 12 12 12C13.6569 12 15 13.1193 15 14.5C15 15.8807 13.6569 17 12 17C10.3431 17 9 15.8807 9 14.5" stroke={`url(#${gradientId})`} strokeWidth="1.5" strokeLinecap="round"></path>
    </svg>
  );
};

const HelpMenuIcon = ({ className }: LandingIconProps) => {
  const gradientId = useId();

  return (
    <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" className={className}>
      <FocusGradientDefs gradientId={gradientId} />
      <circle cx="12" cy="12" r="10" stroke={`url(#${gradientId})`} strokeWidth="1.5"></circle>
      <path d="M10.125 8.875C10.125 7.83947 10.9645 7 12 7C13.0355 7 13.875 7.83947 13.875 8.875C13.875 9.56245 13.505 10.1635 12.9534 10.4899C12.478 10.7711 12 11.1977 12 11.75V13" stroke={`url(#${gradientId})`} strokeWidth="1.5" strokeLinecap="round"></path>
      <circle cx="12" cy="16" r="1" fill={`url(#${gradientId})`}></circle>
    </svg>
  );
};

const MessageMenuIcon = ({ className }: LandingIconProps) => {
  const gradientId = useId();

  return (
    <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" className={className}>
      <FocusGradientDefs gradientId={gradientId} />
      <path d="M8 10.5H16" stroke={`url(#${gradientId})`} strokeWidth="1.5" strokeLinecap="round"></path>
      <path d="M8 14H13" stroke={`url(#${gradientId})`} strokeWidth="1.5" strokeLinecap="round"></path>
      <path d="M7.8 19.5H7C4.79086 19.5 3 17.7091 3 15.5V8.5C3 6.29086 4.79086 4.5 7 4.5H17C19.2091 4.5 21 6.29086 21 8.5V15.5C21 17.7091 19.2091 19.5 17 19.5H12.8L9.5 22V19.5H7.8Z" stroke={`url(#${gradientId})`} strokeWidth="1.5" strokeLinejoin="round"></path>
    </svg>
  );
};

const AffiliateMenuIcon = ({ className }: LandingIconProps) => {
  const gradientId = useId();

  return (
    <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" className={className}>
      <FocusGradientDefs gradientId={gradientId} />
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M12 2.75C6.89137 2.75 2.75 6.89137 2.75 12C2.75 17.1086 6.89137 21.25 12 21.25C17.1086 21.25 21.25 17.1086 21.25 12C21.25 6.89137 17.1086 2.75 12 2.75ZM1.25 12C1.25 6.06294 6.06294 1.25 12 1.25C17.9371 1.25 22.75 6.06294 22.75 12C22.75 17.9371 17.9371 22.75 12 22.75C6.06294 22.75 1.25 17.9371 1.25 12ZM12 5.25C12.4142 5.25 12.75 5.58579 12.75 6V6.31673C14.3804 6.60867 15.75 7.83361 15.75 9.5C15.75 9.91421 15.4142 10.25 15 10.25C14.5858 10.25 14.25 9.91421 14.25 9.5C14.25 8.65573 13.3765 7.75 12 7.75C10.6235 7.75 9.75 8.65573 9.75 9.5C9.75 10.3443 10.6235 11.25 12 11.25C13.9372 11.25 15.75 12.5828 15.75 14.5C15.75 16.1664 14.3804 17.3913 12.75 17.6833V18C12.75 18.4142 12.4142 18.75 12 18.75C11.5858 18.75 11.25 18.4142 11.25 18V17.6833C9.61957 17.3913 8.25 16.1664 8.25 14.5C8.25 14.0858 8.58579 13.75 9 13.75C9.41421 13.75 9.75 14.0858 9.75 14.5C9.75 15.3443 10.6235 16.25 12 16.25C13.3765 16.25 14.25 15.3443 14.25 14.5C14.25 13.6557 13.3765 12.75 12 12.75C10.0628 12.75 8.25 11.4172 8.25 9.5C8.25 7.83361 9.61957 6.60867 11.25 6.31673V6C11.25 5.58579 11.5858 5.25 12 5.25Z"
        fill={`url(#${gradientId})`}
      />
    </svg>
  );
};

import atlasMainPng from '../assets/atlas-main.png';

const AtlasMenuIcon = ({ className }: LandingIconProps) => (
  <img src={atlasMainPng} alt="Atlas" className={className} style={{ width: 34, height: 34, objectFit: 'contain', borderRadius: 8 }} />
);

// Même pictogramme (bouclier + check) que le verdict "Tu peux trader" de
// PropFirmVerdictBanner dans l'app — cohérence entre le menu et le produit.
const PropFirmMenuIcon = ({ className }: LandingIconProps) => {
  const gradientId = useId();

  return (
    <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" className={className}>
      <FocusGradientDefs gradientId={gradientId} />
      <path d="M15.06 10.5a.75.75 0 0 0-1.12-1l-3.011 3.374l-.87-.974a.75.75 0 0 0-1.118 1l1.428 1.6a.75.75 0 0 0 1.119 0z" fill={`url(#${gradientId})`} />
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M12 1.25c-.937 0-1.833.307-3.277.801l-.727.25c-1.481.506-2.625.898-3.443 1.23c-.412.167-.767.33-1.052.495c-.275.16-.55.359-.737.626c-.185.263-.281.587-.341.9c-.063.324-.1.713-.125 1.16c-.048.886-.048 2.102-.048 3.678v1.601c0 6.101 4.608 9.026 7.348 10.224l.027.011c.34.149.66.288 1.027.382c.387.1.799.142 1.348.142c.55 0 .96-.042 1.348-.142c.367-.094.687-.233 1.026-.382l.028-.011c2.74-1.198 7.348-4.123 7.348-10.224V10.39c0-1.576 0-2.792-.048-3.679a9 9 0 0 0-.125-1.16c-.06-.312-.156-.636-.34-.9c-.188-.266-.463-.465-.738-.625a9 9 0 0 0-1.052-.495c-.818-.332-1.962-.724-3.443-1.23l-.727-.25c-1.444-.494-2.34-.801-3.277-.801M9.08 3.514c1.615-.552 2.262-.764 2.92-.764s1.305.212 2.92.764l.572.196c1.513.518 2.616.896 3.39 1.21c.387.158.667.29.864.404q.144.084.208.139c.038.03.053.048.055.05a.4.4 0 0 1 .032.074q.03.082.063.248a7 7 0 0 1 .1.958c.046.841.046 2.015.046 3.624v1.574c0 5.176-3.87 7.723-6.449 8.849c-.371.162-.586.254-.825.315c-.228.059-.506.095-.976.095s-.748-.036-.976-.095c-.24-.06-.454-.153-.825-.315c-2.58-1.126-6.449-3.674-6.449-8.849v-1.574c0-1.609 0-2.783.046-3.624a7 7 0 0 1 .1-.958q.032-.166.063-.248c.018-.05.03-.07.032-.074a.4.4 0 0 1 .055-.05q.064-.055.208-.14c.197-.114.477-.245.864-.402c.774-.315 1.877-.693 3.39-1.21z"
        fill={`url(#${gradientId})`}
      />
    </svg>
  );
};

const ContactMenuIcon = ({ className }: LandingIconProps) => {
  const gradientId = useId();

  return (
    <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" className={className}>
      <FocusGradientDefs gradientId={gradientId} />
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M12 2.75C6.89137 2.75 2.75 6.89137 2.75 12C2.75 13.4811 3.09753 14.8788 3.7148 16.1181C3.96254 16.6155 4.05794 17.2103 3.90163 17.7945L3.30602 20.0205C3.19663 20.4293 3.57066 20.8034 3.97949 20.694L6.20553 20.0984C6.78973 19.9421 7.38451 20.0375 7.88191 20.2852C9.12121 20.9025 10.5189 21.25 12 21.25C17.1086 21.25 21.25 17.1086 21.25 12C21.25 6.89137 17.1086 2.75 12 2.75ZM1.25 12C1.25 6.06294 6.06294 1.25 12 1.25C17.9371 1.25 22.75 6.06294 22.75 12C22.75 17.9371 17.9371 22.75 12 22.75C10.2817 22.75 8.65552 22.3463 7.21315 21.6279C6.99791 21.5207 6.77814 21.4979 6.59324 21.5474L4.3672 22.143C2.84337 22.5507 1.44927 21.1566 1.857 19.6328L2.4526 17.4068C2.50208 17.2219 2.47933 17.0021 2.37213 16.7869C1.65371 15.3445 1.25 13.7183 1.25 12ZM7.25 10.5C7.25 10.0858 7.58579 9.75 8 9.75H16C16.4142 9.75 16.75 10.0858 16.75 10.5C16.75 10.9142 16.4142 11.25 16 11.25H8C7.58579 11.25 7.25 10.9142 7.25 10.5ZM7.25 14C7.25 13.5858 7.58579 13.25 8 13.25H13.5C13.9142 13.25 14.25 13.5858 14.25 14C14.25 14.4142 13.9142 14.75 13.5 14.75H8C7.58579 14.75 7.25 14.4142 7.25 14Z"
        fill={`url(#${gradientId})`}
      />
    </svg>
  );
};

interface NavbarProps {
  /** Couleur de fond personnalisée (optionnel, défaut: 'white') */
  backgroundColor?: string;
  /** Afficher les animations (par défaut: true) */
  showAnimations?: boolean;
  /** Position CSS (optionnel, défaut: 'fixed') */
  position?: 'fixed' | 'relative' | 'sticky' | 'static';
  /** Position top (optionnel, défaut: 0) */
  top?: string | number;
  /** Variante de navigation à utiliser */
  navigationVariant?: 'default' | 'landing';
  /** Mode sombre (texte blanc, hover clair) */
  isDark?: boolean;
}

type LandingMenuKey = 'products' | 'company' | null;
type ProductKey = 'journal' | 'propfirm' | 'backtesting' | 'platforms' | 'atlas';

const landingProducts: Array<{
  key: ProductKey;
  /** Clé i18n : `landing.nav.products_items.<key>` (titre + description). */
  href: string;
  icon: React.ComponentType<LandingIconProps>;
  /** Réservé au module Prop Firm pour l'instant — même pastille que la
      bannière et la section dédiée de la landing page. */
  badge?: string;
  children: Array<{ labelKey: string; href: string }>;
}> = [
  {
    key: 'journal',
    href: '/features',
    icon: JournalMenuIcon,
    children: [
      { labelKey: 'discover', href: '/features#statistiques' },
      { labelKey: 'pricing', href: '/pricing' },
      { labelKey: 'changelog', href: '/changelog' },
    ],
  },
  {
    key: 'propfirm',
    href: '/features#propfirm',
    icon: PropFirmMenuIcon,
    badge: 'new',
    children: [
      { labelKey: 'discover', href: '/features#propfirm' },
      { labelKey: 'pricing', href: '/pricing' },
      { labelKey: 'free_trial', href: '/register' },
    ],
  },
  {
    key: 'backtesting',
    href: '/backtesting',
    icon: BacktestMenuIcon,
    children: [
      { labelKey: 'discover', href: '/backtesting' },
      { labelKey: 'available_pairs', href: '/backtesting#instruments' },
      { labelKey: 'free_trial', href: '/register' },
    ],
  },
  {
    key: 'platforms',
    href: '/brokers',
    icon: PlatformMenuIcon,
    children: [
      { labelKey: 'platform_list', href: '/brokers' },
      { labelKey: 'help_center', href: '/help' },
      { labelKey: 'contact_us', href: '/contact' },
    ],
  },
  {
    key: 'atlas',
    href: '/features#atlas',
    icon: AtlasMenuIcon,
    children: [
      { labelKey: 'discover_atlas', href: '/features' },
      { labelKey: 'free_trial', href: '/register' },
    ],
  },
];

const landingCompanyLinks = [
  { key: 'blog', href: '/blog', icon: JournalMenuIcon },
  { key: 'affiliate', href: '/affiliate', icon: AffiliateMenuIcon },
  { key: 'contact', href: '/contact', icon: ContactMenuIcon },
];

export const Navbar: React.FC<NavbarProps> = ({ 
  backgroundColor = 'white',
  showAnimations = true,
  position = 'fixed',
  top = 0,
  navigationVariant = 'landing',
  isDark = false,
}) => {
  const textCls = isDark ? 'text-white' : 'text-gray-900';
  const hoverTextCls = isDark ? 'hover:text-gray-300' : 'hover:text-gray-600';
  const hoverBgCls = isDark ? 'hover:bg-white/10' : 'hover:bg-gray-200/50';
  const iconColor = isDark ? '#FFFFFF' : '#111827';
  // Landing nav dark vars
  const navItemCls = isDark ? 'text-white' : 'text-slate-700';
  const navItemHover = isDark ? 'hover:bg-white/10 hover:text-white' : 'hover:bg-slate-100 hover:text-slate-950';
  const navItemActive = isDark ? 'bg-white/10 text-white' : 'bg-slate-100 text-slate-950';
  const dropBg = isDark ? '#16181C' : '#ffffff';
  const dropItemBg = isDark ? 'rgba(255,255,255,0.05)' : '#f8faff';
  const dropBorder = isDark ? 'rgba(255,255,255,0.08)' : 'rgb(226,232,240)';
  const dropText = isDark ? '#ffffff' : '#0f172a';
  const dropSubtext = isDark ? 'rgb(148,163,184)' : 'rgb(71,85,105)';
  const dropLinkBg = isDark ? 'rgba(255,255,255,0.06)' : '#ffffff';
  const dropLinkHoverBg = isDark ? 'rgba(255,255,255,0.1)' : '#f8fafc';
  const mobileBorder = isDark ? 'border-white/10' : 'border-slate-200';
  const mobileMenuBg = isDark ? 'bg-[#0B0B0C]' : 'bg-white';
  const { t } = useTranslation();
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [desktopMenuOpen, setDesktopMenuOpen] = useState<LandingMenuKey>(null);
  const [activeProduct, setActiveProduct] = useState<ProductKey>('journal');
  const [mobileView, setMobileView] = useState<'root' | 'products' | 'company' | 'product-detail'>('root');
  const [mobileProduct, setMobileProduct] = useState<ProductKey>('journal');
  const landingNavRef = useRef<HTMLElement | null>(null);
  const desktopMenuCloseTimeoutRef = useRef<number | null>(null);

  const activeProductItem = useMemo(
    () => landingProducts.find((item) => item.key === activeProduct) ?? landingProducts[0],
    [activeProduct],
  );

  const mobileProductItem = useMemo(
    () => landingProducts.find((item) => item.key === mobileProduct) ?? landingProducts[0],
    [mobileProduct],
  );

  const registerHref = '/register';
  const handleRegisterClick = (position: string) => {
    trackEvent('cta_navbar_clicked', { position });
    closeAllMenus();
  };

  useEffect(() => {
    document.getElementById('landing-nav-dark-override')?.remove();
    if (!isDark || navigationVariant !== 'landing') return;
    const style = document.createElement('style');
    style.id = 'landing-nav-dark-override';
    style.textContent = `
      html:not(.dark) header.landing-nav-dark { background-color: #0B0B0C !important; }
      html:not(.dark) header.landing-nav-dark > div { background-color: #0B0B0C !important; }
      html:not(.dark) header.landing-nav-dark > div > div { background-color: #0B0B0C !important; }
      html:not(.dark) header.landing-nav-dark > div > div > div { background-color: #0B0B0C !important; }
      html:not(.dark) header.landing-nav-dark nav a,
      html:not(.dark) header.landing-nav-dark nav button,
      html:not(.dark) header.landing-nav-dark nav button span,
      html:not(.dark) header.landing-nav-dark nav a span {
        color: #ffffff !important;
        -webkit-text-fill-color: #ffffff !important;
      }
      html:not(.dark) header.landing-nav-dark nav a svg,
      html:not(.dark) header.landing-nav-dark nav button svg {
        color: #ffffff !important;
        stroke: #ffffff !important;
      }
      html:not(.dark) header.landing-nav-dark nav a:hover,
      html:not(.dark) header.landing-nav-dark nav button:hover,
      html:not(.dark) header.landing-nav-dark nav button:hover span,
      html:not(.dark) header.landing-nav-dark nav a:hover span {
        color: #ffffff !important;
        -webkit-text-fill-color: #ffffff !important;
      }
      html:not(.dark) header.landing-nav-dark nav a:hover svg,
      html:not(.dark) header.landing-nav-dark nav button:hover svg {
        color: #ffffff !important;
        stroke: #ffffff !important;
      }
      html:not(.dark) header.landing-nav-dark .landing-login-link,
      html:not(.dark) header.landing-nav-dark .landing-login-link:hover,
      html:not(.dark) header.landing-nav-dark .landing-login-link:focus-visible {
        color: #ffffff !important;
        -webkit-text-fill-color: #ffffff !important;
      }
      html:not(.dark) header.landing-nav-dark .landing-mobile-menu { background-color: #0B0B0C !important; }
      html:not(.dark) header.landing-nav-dark .landing-mobile-menu > div { background-color: #0B0B0C !important; }
      html:not(.dark) header.landing-nav-dark .landing-mobile-menu a { color: #ffffff !important; -webkit-text-fill-color: #ffffff !important; }
      html:not(.dark) header.landing-nav-dark .landing-mobile-menu button { color: #ffffff !important; -webkit-text-fill-color: #ffffff !important; }
      html:not(.dark) header.landing-nav-dark .landing-mobile-menu span { color: #ffffff !important; -webkit-text-fill-color: #ffffff !important; }
      html:not(.dark) header.landing-nav-dark .landing-mobile-menu div { color: #ffffff !important; }
      html:not(.dark) header.landing-nav-dark > div > div svg { color: #ffffff !important; -webkit-text-fill-color: initial !important; }
      html:not(.dark) header.landing-nav-dark .landing-mobile-menu svg { color: #94a3b8 !important; -webkit-text-fill-color: initial !important; }
    `;
    document.getElementById('landing-nav-dark-override')?.remove();
    document.head.appendChild(style);
    return () => { style.remove(); };
  }, [isDark, navigationVariant]);

  useEffect(() => {
    setMobileMenuOpen(false);
    setDesktopMenuOpen(null);
    setMobileView('root');
  }, [location.pathname]);

  useEffect(() => {
    return () => {
      if (desktopMenuCloseTimeoutRef.current !== null) {
        window.clearTimeout(desktopMenuCloseTimeoutRef.current);
      }
    };
  }, []);

  useEffect(() => {
    if (navigationVariant !== 'landing') {
      return;
    }

    const handlePointerDown = (event: MouseEvent) => {
      if (!landingNavRef.current?.contains(event.target as Node)) {
        setDesktopMenuOpen(null);
      }
    };

    document.addEventListener('mousedown', handlePointerDown);
    return () => document.removeEventListener('mousedown', handlePointerDown);
  }, [navigationVariant]);

  const handleLogout = async () => {
    try {
      await authService.logout();
      navigate('/');
    } catch (error) {
      // console.error supprimé
    }
  };

  const closeAllMenus = () => {
    if (desktopMenuCloseTimeoutRef.current !== null) {
      window.clearTimeout(desktopMenuCloseTimeoutRef.current);
      desktopMenuCloseTimeoutRef.current = null;
    }
    setMobileMenuOpen(false);
    setDesktopMenuOpen(null);
    setMobileView('root');
  };

  const clearDesktopMenuCloseTimeout = () => {
    if (desktopMenuCloseTimeoutRef.current !== null) {
      window.clearTimeout(desktopMenuCloseTimeoutRef.current);
      desktopMenuCloseTimeoutRef.current = null;
    }
  };

  const openDesktopMenu = (menu: LandingMenuKey) => {
    clearDesktopMenuCloseTimeout();
    setDesktopMenuOpen(menu);
  };

  const scheduleDesktopMenuClose = () => {
    clearDesktopMenuCloseTimeout();
    desktopMenuCloseTimeoutRef.current = window.setTimeout(() => {
      setDesktopMenuOpen(null);
      desktopMenuCloseTimeoutRef.current = null;
    }, 180);
  };

  if (navigationVariant === 'landing') {
    return (
      <>
      <header
        ref={landingNavRef}
        className={`landing-nav ${isDark ? 'landing-nav-dark' : ''} ${position} left-0 right-0 z-50`}
        style={{
          backgroundColor: isDark ? '#0B0B0C' : backgroundColor,
          top,
          boxShadow: isDark ? '0 1px 0 rgba(255,255,255,0.06)' : '0 12px 34px rgba(15, 23, 42, 0.06)',
          paddingTop: 'env(safe-area-inset-top)',
          paddingLeft: 'env(safe-area-inset-left)',
          paddingRight: 'env(safe-area-inset-right)',
        }}
      >
        <div className="mx-auto flex max-w-[1440px] items-center gap-4 px-4 py-3 sm:px-6 lg:px-10">
          <div className="flex min-w-0 flex-1 items-center gap-3 lg:flex-none lg:basis-[240px]">
            <Link to="/" className="inline-flex items-center" onClick={closeAllMenus}>
              <FastImage
                src={logoSvg}
                srcWebP={logoSvgWebP}
                srcSmWebP={logoSvgSmWebP}
                alt="FocusPips"
                className="h-6 w-auto object-contain sm:h-8 lg:h-9"
                priority={true}
                width={600}
                height={114}
              />
            </Link>
          </div>

          <nav className="hidden min-w-0 flex-1 items-center justify-center gap-1 lg:flex">
            <div
              className="relative"
              onMouseEnter={() => {
                openDesktopMenu('products');
                setActiveProduct((current) => current ?? 'journal');
              }}
              onMouseLeave={scheduleDesktopMenuClose}
            >
              <button
                type="button"
                className={`inline-flex items-center gap-2 whitespace-nowrap rounded-[14px] px-4 py-2 text-sm font-medium transition-colors ${navItemCls} ${desktopMenuOpen === 'products' ? navItemActive : navItemHover}`}
                onClick={() => setDesktopMenuOpen((current) => current === 'products' ? null : 'products')}
                aria-expanded={desktopMenuOpen === 'products'}
              >
                {t('landing.nav.products')}
                <ChevronDown className={`h-4 w-4 transition-transform ${desktopMenuOpen === 'products' ? 'rotate-180' : ''}`} />
              </button>
            </div>

            <Link
              to="/pricing"
              className={`whitespace-nowrap rounded-[14px] px-4 py-2 text-sm font-medium transition-colors ${navItemCls} ${navItemHover}`}
              onClick={closeAllMenus}
            >
              {t('landing.nav.pricing')}
            </Link>

            <div className="relative" onMouseEnter={() => openDesktopMenu('company')} onMouseLeave={scheduleDesktopMenuClose}>
              <button
                type="button"
                className={`inline-flex items-center gap-2 whitespace-nowrap rounded-[14px] px-4 py-2 text-sm font-medium transition-colors ${navItemCls} ${desktopMenuOpen === 'company' ? navItemActive : navItemHover}`}
                onClick={() => setDesktopMenuOpen((current) => current === 'company' ? null : 'company')}
                aria-expanded={desktopMenuOpen === 'company'}
              >
                {t('landing.nav.company')}
                <ChevronDown className={`h-4 w-4 transition-transform ${desktopMenuOpen === 'company' ? 'rotate-180' : ''}`} />
              </button>
            </div>

            <Link
              to="/help"
              className={`whitespace-nowrap rounded-[14px] px-4 py-2 text-sm font-medium transition-colors ${navItemCls} ${navItemHover}`}
              onClick={closeAllMenus}
            >
              {t('landing.nav.help_center')}
            </Link>
          </nav>

          <div className="hidden items-center justify-end gap-3 lg:flex lg:shrink-0 lg:basis-auto">
            {/* Sélecteur de langue — dernier élément avant les CTA : c'est la
                convention sur les sites multilingues, et il reste hors du chemin
                du bouton d'inscription. */}
            <LanguageFlagSelector isDark={isDark} />
            {user ? (
              <>
                <button
                  onClick={handleLogout}
                  className="inline-flex h-10 w-10 items-center justify-center transition-colors"
                  style={{ color: isDark ? 'rgba(255,255,255,0.7)' : '#334155' }}
                  title={t('landing.nav.logout')}
                >
                  <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="m16 17 5-5-5-5"/>
                    <path d="M21 12H9"/>
                    <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"/>
                  </svg>
                </button>
                <button
                  onClick={() => navigate('/dashboard')}
                  className="inline-flex items-center justify-center whitespace-nowrap rounded-[16px] px-5 py-2 text-sm font-semibold shadow-lg shadow-fuchsia-500/15 transition-transform hover:-translate-y-0.5"
                  style={{
                    background: 'linear-gradient(120deg, var(--purple) 0%, var(--pink) 50%, var(--orange) 100%)', backgroundSize: '200% 200%', animation: 'gradientShift 4s ease infinite',
                    color: 'white',
                  }}
                >
                  {t('landing.nav.dashboard')}
                </button>
              </>
            ) : (
              <>
                <Link
                  to="/login"
                  className={`landing-login-link whitespace-nowrap rounded-[14px] px-4 py-2 text-sm font-medium transition-colors ${navItemCls} ${navItemHover}`}
                  style={isDark ? { color: '#ffffff' } : undefined}
                  onClick={closeAllMenus}
                >
                  {t('landing.nav.login')}
                </Link>
                <Link
                  to={registerHref}
                  className="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-[16px] px-5 py-2 text-sm font-semibold shadow-lg shadow-fuchsia-500/15 transition-transform hover:-translate-y-0.5"
                  onClick={() => handleRegisterClick('desktop')}
                  style={{
                    background: 'linear-gradient(120deg, var(--purple) 0%, var(--pink) 50%, var(--orange) 100%)', backgroundSize: '200% 200%', animation: 'gradientShift 4s ease infinite',
                    color: '#ffffff',
                  }}
                >
                  {t('landing.nav.start_trial')}
                  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true">
                    <path d="M3 8H13M13 8L9 4M13 8L9 12" stroke="#ffffff" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </Link>
              </>
            )}
          </div>

          <div className="flex items-center gap-2 lg:hidden" style={{ backgroundColor: isDark ? '#0B0B0C' : undefined }}>
            <LanguageFlagSelector isDark={isDark} />
            {!user && (
              <Link
                to={registerHref}
                className="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-[14px] px-3 py-1.5 text-xs font-semibold shadow-lg shadow-fuchsia-500/15 sm:px-4 sm:text-sm"
                onClick={() => handleRegisterClick('mobile')}
                style={{
                  background: 'linear-gradient(120deg, var(--purple) 0%, var(--pink) 50%, var(--orange) 100%)', backgroundSize: '200% 200%', animation: 'gradientShift 4s ease infinite',
                  color: '#ffffff',
                }}
              >
                {t('landing.nav.start')}
                <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true">
                  <path d="M3 8H13M13 8L9 4M13 8L9 12" stroke="#ffffff" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </Link>
            )}
            <button
              onClick={() => {
                setMobileMenuOpen((current) => !current);
                setMobileView('root');
              }}
              className="inline-flex items-center justify-center p-1 transition-colors"
              style={{ color: isDark ? '#ffffff' : '#0f172a' }}
              aria-label={t('landing.nav.menu')}
              aria-expanded={mobileMenuOpen}
            >
              {mobileMenuOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
            </button>
          </div>
        </div>

        {desktopMenuOpen === 'products' && (
          <div
            className={`absolute left-1/2 top-full hidden w-[min(1120px,calc(100vw-2rem))] -translate-x-1/2 rounded-[26px] p-4 shadow-[0_30px_80px_rgba(15,23,42,0.18)] lg:block`}
            style={{ background: dropBg, border: `1px solid ${dropBorder}` }}
            onMouseEnter={clearDesktopMenuCloseTimeout}
            onMouseLeave={scheduleDesktopMenuClose}
          >
            <div className="grid grid-cols-[1.2fr_0.9fr] gap-4">
              <div className="grid gap-3">
                {landingProducts.map((item) => {
                  const Icon = item.icon;
                  const isActive = activeProduct === item.key;
                  return (
                    <button
                      key={item.key}
                      type="button"
                      className="flex items-start gap-4 rounded-[20px] px-5 py-4 text-left transition-all"
                      style={{
                        background: isActive ? dropItemBg : 'transparent',
                        boxShadow: isActive ? '0 14px 32px rgba(15,23,42,0.06)' : 'none',
                      }}
                      onMouseEnter={() => setActiveProduct(item.key)}
                      onFocus={() => setActiveProduct(item.key)}
                      onClick={() => {
                        setDesktopMenuOpen(null);
                        navigate(item.href);
                      }}
                    >
                      <span className="mt-1 inline-flex items-center justify-center" style={{ color: dropText }}>
                        <Icon className="h-8 w-8" />
                      </span>
                      <span className="flex-1">
                        <span className="flex items-center justify-between gap-3 text-base font-semibold" style={{ color: dropText }}>
                          <span className="inline-flex items-center gap-2">
                            {t(`landing.nav.products_items.${item.key}.title`)}
                            {item.badge && (
                              <span className="inline-flex rounded-full" style={{ padding: '2px', background: 'linear-gradient(135deg, #3d2a6e 0%, #7C76FF 40%, #c45aaa 70%, #FF7AD9 100%)' }}>
                                <span className="flex items-center rounded-full" style={{ background: isDark ? '#0a0a0d' : '#ffffff', padding: '3px 8px', fontSize: '0.58rem', fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.08em', lineHeight: 1, color: isDark ? '#ffffff' : '#454653' }}>
                                  {t('landing.common.new')}
                                </span>
                              </span>
                            )}
                          </span>
                          <ArrowRight className="h-4 w-4" style={{ color: dropSubtext }} />
                        </span>
                        <span className="mt-1 block text-sm leading-6" style={{ color: dropSubtext }}>{t(`landing.nav.products_items.${item.key}.description`)}</span>
                      </span>
                    </button>
                  );
                })}
              </div>

              <div className="rounded-[22px] p-7 shadow-[0_20px_40px_rgba(15,23,42,0.08)]" style={{ background: dropItemBg, color: dropText }}>
                <div>
                  <h3 className="flex items-center gap-2.5 text-2xl font-semibold" style={{ color: dropText }}>
                    {t(`landing.nav.products_items.${activeProductItem.key}.title`)}
                    {activeProductItem.badge && (
                      <span className="inline-flex rounded-full" style={{ padding: '2px', background: 'linear-gradient(135deg, #3d2a6e 0%, #7C76FF 40%, #c45aaa 70%, #FF7AD9 100%)' }}>
                        <span className="flex items-center rounded-full" style={{ background: isDark ? '#0a0a0d' : '#ffffff', padding: '4px 10px', fontSize: '0.62rem', fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.08em', lineHeight: 1, color: isDark ? '#ffffff' : '#454653' }}>
                          {t('landing.common.new')}
                        </span>
                      </span>
                    )}
                  </h3>
                  <p className="mt-3 max-w-md text-sm leading-6" style={{ color: dropSubtext }}>{t(`landing.nav.products_items.${activeProductItem.key}.description`)}</p>
                </div>

                <div className="mt-6 grid gap-2">
                  {activeProductItem.children.map((child) => (
                    <Link
                      key={child.labelKey}
                      to={child.href}
                      className="flex items-center justify-between rounded-[16px] px-4 py-3 text-sm font-medium transition-colors"
                      style={{ background: dropLinkBg, color: dropText }}
                      onMouseEnter={e => (e.currentTarget.style.background = dropLinkHoverBg)}
                      onMouseLeave={e => (e.currentTarget.style.background = dropLinkBg)}
                      onClick={closeAllMenus}
                    >
                      {t(`landing.nav.links.${child.labelKey}`)}
                      <ArrowRight className="h-4 w-4" style={{ color: dropSubtext }} />
                    </Link>
                  ))}
                </div>
              </div>
            </div>
          </div>
        )}

        {desktopMenuOpen === 'company' && (
          <div
            className="absolute left-1/2 top-full hidden w-[min(980px,calc(100vw-2rem))] -translate-x-1/2 rounded-[26px] p-4 shadow-[0_30px_80px_rgba(15,23,42,0.18)] lg:block"
            style={{ background: dropBg, border: `1px solid ${dropBorder}` }}
            onMouseEnter={clearDesktopMenuCloseTimeout}
            onMouseLeave={scheduleDesktopMenuClose}
          >
            <div className="grid grid-cols-[1.2fr_0.8fr] gap-4">
              <div className="grid gap-3">
                {landingCompanyLinks.map((item) => {
                  const Icon = item.icon;
                  return (
                    <Link
                      key={item.key}
                      to={item.href}
                      className="flex items-start gap-4 rounded-[20px] px-5 py-4 text-left transition-all"
                      style={{ background: dropItemBg }}
                      onMouseEnter={e => (e.currentTarget.style.background = dropLinkHoverBg)}
                      onMouseLeave={e => (e.currentTarget.style.background = dropItemBg)}
                      onClick={closeAllMenus}
                    >
                      <span className="mt-1 inline-flex items-center justify-center" style={{ color: '#7c76ff' }}>
                        <Icon className="h-8 w-8" />
                      </span>
                      <span className="flex-1">
                        <span className="flex items-center justify-between gap-3 text-base font-semibold" style={{ color: dropText }}>
                          {t(`landing.nav.company_items.${item.key}.title`)}
                          <ArrowRight className="h-4 w-4" style={{ color: dropSubtext }} />
                        </span>
                        <span className="mt-1 block text-sm leading-6" style={{ color: dropSubtext }}>{t(`landing.nav.company_items.${item.key}.description`)}</span>
                      </span>
                    </Link>
                  );
                })}
              </div>

              <div className="rounded-[22px] p-7" style={{ background: dropItemBg, border: `1px solid ${dropBorder}` }}>
                <h3 className="text-2xl font-semibold" style={{ color: dropText }}>{t('landing.nav.need_help_title')}</h3>
                <p className="mt-3 text-sm leading-6" style={{ color: dropSubtext }}>
                  {t('landing.nav.need_help_text')}
                </p>
                <div className="mt-6 grid gap-2">
                  <Link to="/help" className="flex items-center justify-between rounded-[16px] px-4 py-3 text-sm font-medium transition-colors" style={{ background: dropLinkBg, color: dropText }} onMouseEnter={e => (e.currentTarget.style.background = dropLinkHoverBg)} onMouseLeave={e => (e.currentTarget.style.background = dropLinkBg)} onClick={closeAllMenus}>
                    {t('landing.nav.help_center')}
                    <ArrowRight className="h-4 w-4" style={{ color: dropSubtext }} />
                  </Link>
                  <Link to="/contact" className="flex items-center justify-between rounded-[16px] px-4 py-3 text-sm font-medium transition-colors" style={{ background: dropLinkBg, color: dropText }} onMouseEnter={e => (e.currentTarget.style.background = dropLinkHoverBg)} onMouseLeave={e => (e.currentTarget.style.background = dropLinkBg)} onClick={closeAllMenus}>
                    {t('landing.nav.contact')}
                    <ArrowRight className="h-4 w-4" style={{ color: dropSubtext }} />
                  </Link>
                </div>
              </div>
            </div>
          </div>
        )}

        <div
          className={`landing-nav-mobile ${isDark ? 'landing-mobile-menu' : ''} overflow-hidden border-t transition-all duration-300 ${mobileBorder} ${mobileMenuBg} ${mobileMenuOpen ? 'max-h-[90vh] opacity-100' : 'max-h-0 opacity-0'}`}
          style={isDark ? { backgroundColor: '#0B0B0C' } : undefined}
        >
          <div className="px-4 py-4 sm:px-6" style={{ backgroundColor: isDark ? '#0B0B0C' : undefined, color: isDark ? '#ffffff' : undefined }}>
            {mobileView === 'root' && (
              <div className="grid gap-2">
                <button
                  type="button"
                  className={`flex items-center justify-between rounded-[16px] px-4 py-3 text-left text-sm font-medium transition-colors ${navItemCls} ${navItemHover}`}
                  style={isDark ? { color: '#ffffff' } : undefined}
                  onClick={() => setMobileView('products')}
                >
                  {t('landing.nav.products')}
                  <ArrowRight className="h-4 w-4" style={{ color: dropSubtext }} />
                </button>
                <button
                  type="button"
                  className={`flex items-center justify-between rounded-[16px] px-4 py-3 text-left text-sm font-medium transition-colors ${navItemCls} ${navItemHover}`}
                  style={isDark ? { color: '#ffffff' } : undefined}
                  onClick={() => setMobileView('company')}
                >
                  {t('landing.nav.company')}
                  <ArrowRight className="h-4 w-4" style={{ color: dropSubtext }} />
                </button>
                <Link to="/pricing" className={`rounded-[16px] px-4 py-3 text-sm font-medium transition-colors ${navItemCls} ${navItemHover}`} style={isDark ? { color: '#ffffff' } : undefined} onClick={closeAllMenus}>
                  {t('landing.nav.pricing')}
                </Link>
                <Link to="/help" className={`rounded-[16px] px-4 py-3 text-sm font-medium transition-colors ${navItemCls} ${navItemHover}`} style={isDark ? { color: '#ffffff' } : undefined} onClick={closeAllMenus}>
                  {t('landing.nav.help_center')}
                </Link>

                <div className={`mt-3 grid gap-2 border-t pt-3 ${mobileBorder}`}>
                  {user ? (
                    <>
                      <button
                        onClick={() => { navigate('/dashboard'); closeAllMenus(); }}
                        className="inline-flex items-center justify-center rounded-[16px] px-4 py-3 text-sm font-semibold transition-transform hover:-translate-y-0.5"
                        style={{ background: 'linear-gradient(120deg, var(--purple) 0%, var(--pink) 50%, var(--orange) 100%)', backgroundSize: '200% 200%', animation: 'gradientShift 4s ease infinite', color: 'white' }}
                      >
                        {t('landing.nav.dashboard')}
                      </button>
                      <button onClick={() => { handleLogout(); closeAllMenus(); }} className={`rounded-[16px] px-4 py-3 text-left text-sm font-medium transition-colors ${navItemCls} ${navItemHover}`} style={isDark ? { color: '#ffffff' } : undefined}>
                        {t('landing.nav.logout')}
                      </button>
                    </>
                  ) : (
                    <>
                      <Link to="/login" className={`rounded-[16px] px-4 py-3 text-sm font-medium transition-colors ${navItemCls} ${navItemHover}`} style={isDark ? { color: '#ffffff' } : undefined} onClick={closeAllMenus}>
                        {t('landing.nav.login')}
                      </Link>
                      <Link
                        to={registerHref}
                        className="inline-flex items-center justify-center rounded-[16px] px-4 py-3 text-sm font-semibold"
                        onClick={() => handleRegisterClick('mobile_menu')}
                        style={{
                          background: 'linear-gradient(120deg, var(--purple) 0%, var(--pink) 50%, var(--orange) 100%)', backgroundSize: '200% 200%', animation: 'gradientShift 4s ease infinite',
                          color: '#ffffff',
                        }}
                      >
                        {t('landing.nav.free_trial')}
                      </Link>
                    </>
                  )}
                </div>
              </div>
            )}

            {mobileView === 'products' && (
              <div className="grid gap-3">
                <button type="button" className={`inline-flex items-center gap-2 rounded-[16px] px-4 py-2 text-sm font-medium transition-colors ${navItemCls} ${navItemHover}`} style={isDark ? { color: '#ffffff' } : undefined} onClick={() => setMobileView('root')}>
                  <ArrowRight className="h-4 w-4 rotate-180" />
                  {t('landing.nav.products')}
                </button>

                {landingProducts.map((item) => {
                  const Icon = item.icon;
                  return (
                    <button
                      key={item.key}
                      type="button"
                      className="flex items-start gap-4 rounded-[18px] px-4 py-4 text-left transition-colors"
                      style={{ background: dropItemBg }}
                      onMouseEnter={e => (e.currentTarget.style.background = dropLinkHoverBg)}
                      onMouseLeave={e => (e.currentTarget.style.background = dropItemBg)}
                      onClick={() => {
                        setMobileProduct(item.key);
                        setMobileView('product-detail');
                      }}
                    >
                      <span className="inline-flex items-center justify-center" style={{ color: dropText }}>
                        <Icon className="h-7 w-7" />
                      </span>
                      <span className="flex-1">
                        <span className="flex items-center justify-between gap-3 text-sm font-semibold" style={{ color: dropText }}>
                          <span className="inline-flex items-center gap-2">
                            {t(`landing.nav.products_items.${item.key}.title`)}
                            {item.badge && (
                              <span className="inline-flex rounded-full" style={{ padding: '2px', background: 'linear-gradient(135deg, #3d2a6e 0%, #7C76FF 40%, #c45aaa 70%, #FF7AD9 100%)' }}>
                                <span className="flex items-center rounded-full" style={{ background: isDark ? '#0a0a0d' : '#ffffff', padding: '3px 8px', fontSize: '0.58rem', fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.08em', lineHeight: 1, color: isDark ? '#ffffff' : '#454653' }}>
                                  {t('landing.common.new')}
                                </span>
                              </span>
                            )}
                          </span>
                          <ArrowRight className="h-4 w-4" style={{ color: dropSubtext }} />
                        </span>
                        <span className="mt-1 block text-sm leading-6" style={{ color: dropSubtext }}>{t(`landing.nav.products_items.${item.key}.description`)}</span>
                      </span>
                    </button>
                  );
                })}

              </div>
            )}

            {mobileView === 'product-detail' && (
              <div className="grid gap-3">
                <button type="button" className={`inline-flex items-center gap-2 rounded-[16px] px-4 py-2 text-sm font-medium transition-colors ${navItemCls} ${navItemHover}`} style={isDark ? { color: '#ffffff' } : undefined} onClick={() => setMobileView('products')}>
                  <ArrowRight className="h-4 w-4 rotate-180" />
                  {t(`landing.nav.products_items.${mobileProductItem.key}.title`)}
                </button>

                <Link to={mobileProductItem.href} className="rounded-[18px] px-4 py-4 text-sm font-semibold transition-colors" style={{ background: dropItemBg, color: dropText }} onClick={closeAllMenus}>
                  {t('landing.nav.main_page')}
                </Link>

                {mobileProductItem.children.map((child) => (
                  <Link key={child.labelKey} to={child.href} className="rounded-[18px] px-4 py-4 text-sm font-medium transition-colors" style={{ background: dropItemBg, color: dropSubtext }} onClick={closeAllMenus}>
                    {t(`landing.nav.links.${child.labelKey}`)}
                  </Link>
                ))}
              </div>
            )}

            {mobileView === 'company' && (
              <div className="grid gap-3">
                <button type="button" className={`inline-flex items-center gap-2 rounded-[16px] px-4 py-2 text-sm font-medium transition-colors ${navItemCls} ${navItemHover}`} style={isDark ? { color: '#ffffff' } : undefined} onClick={() => setMobileView('root')}>
                  <ArrowRight className="h-4 w-4 rotate-180" />
                  {t('landing.nav.company')}
                </button>

                {landingCompanyLinks.map((item) => {
                  const Icon = item.icon;
                  return (
                    <Link
                      key={item.key}
                      to={item.href}
                      className="flex items-start gap-4 rounded-[18px] px-4 py-4 transition-colors"
                      style={{ background: dropItemBg }}
                      onMouseEnter={e => (e.currentTarget.style.background = dropLinkHoverBg)}
                      onMouseLeave={e => (e.currentTarget.style.background = dropItemBg)}
                      onClick={closeAllMenus}
                    >
                      <span className="inline-flex items-center justify-center" style={{ color: '#7c76ff' }}>
                        <Icon className="h-6 w-6" />
                      </span>
                      <span>
                        <span className="block text-sm font-semibold" style={{ color: dropText }}>{t(`landing.nav.company_items.${item.key}.title`)}</span>
                        <span className="mt-1 block text-sm leading-6" style={{ color: dropSubtext }}>{t(`landing.nav.company_items.${item.key}.description`)}</span>
                      </span>
                    </Link>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      </header>
      </>
    );
  }

  return (
    <header 
      className={`${position} left-0 right-0 z-50`} 
      style={{ 
        backgroundColor: backgroundColor,
        top: top,
        paddingTop: 'env(safe-area-inset-top)',
        paddingLeft: 'env(safe-area-inset-left)',
        paddingRight: 'env(safe-area-inset-right)',
      }}
    >
      <div className="max-w-[1920px] mx-auto px-6 sm:px-12 lg:px-24">
        <div className="flex items-center justify-between py-2 sm:py-0">
          {/* Logo */}
          <div className="flex items-center">
            <Link to="/">
              <FastImage 
                src={logoSvg} 
                srcWebP={logoSvgWebP}
                alt="FocusPips" 
                className="h-8 sm:h-9 lg:h-10 w-auto object-contain"
                priority={true}
                width={6000}
                height={3375}
              />
            </Link>
          </div>

          {/* Navigation */}
          <div className="flex items-center gap-2 sm:gap-4">
            {/* Menu hamburger - visible uniquement sur mobile */}
            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="p-2 sm:hidden transition-all duration-300 hover:scale-105 active:scale-95"
              aria-label="Menu"
            >
              {mobileMenuOpen ? (
                <X className={`h-6 w-6 ${textCls}`} />
              ) : (
                <Menu className={`h-6 w-6 ${textCls}`} />
              )}
            </button>

            {/* Liens desktop */}
            <Link 
              to="/help" 
              className={`px-3 py-2 ${textCls} ${hoverTextCls} transition-colors text-sm sm:text-base hidden sm:inline-flex`}
              style={showAnimations ? { animation: 'fadeInUp 0.8s ease-out 0.2s both' } : {}}
            >
              FAQ
            </Link>
            <Link 
              to="/brokers" 
              className={`px-3 py-2 ${textCls} ${hoverTextCls} transition-colors text-sm sm:text-base hidden sm:inline-flex`}
              style={showAnimations ? { animation: 'fadeInUp 0.8s ease-out 0.3s both' } : {}}
            >
              Plateformes supportées
            </Link>
            <Link 
              to="/pricing" 
              className={`px-3 py-2 ${textCls} ${hoverTextCls} transition-colors text-sm sm:text-base hidden sm:inline-flex`}
              style={showAnimations ? { animation: 'fadeInUp 0.8s ease-out 0.4s both' } : {}}
            >
              Tarifs
            </Link>
            <Link 
              to="/blog" 
              className={`px-3 py-2 ${textCls} ${hoverTextCls} transition-colors text-sm sm:text-base hidden sm:inline-flex`}
              style={showAnimations ? { animation: 'fadeInUp 0.8s ease-out 0.5s both' } : {}}
            >
              Blog
            </Link>
            <Link 
              to="/affiliate" 
              className={`px-3 py-2 ${textCls} ${hoverTextCls} transition-colors text-sm sm:text-base hidden sm:inline-flex`}
              style={showAnimations ? { animation: 'fadeInUp 0.8s ease-out 0.6s both' } : {}}
            >
              Affiliation
            </Link>

            {/* Boutons utilisateur ou authentification */}
            {user ? (
              <>
                <button
                  onClick={handleLogout}
                  className={`p-1.5 sm:p-2 rounded-full ${isDark ? 'hover:bg-white/10' : 'hover:bg-gray-200'} transition-colors hidden sm:flex items-center justify-center`}
                  title={t('landing.nav.logout')}
                >
                  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="h-4 w-4 sm:h-5 sm:w-5" style={{ color: iconColor }}>
                    <path d="m16 17 5-5-5-5"/>
                    <path d="M21 12H9"/>
                    <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"/>
                  </svg>
                </button>
                <button
                  onClick={() => navigate('/dashboard')}
                  className="bento-button hidden sm:inline-flex items-center justify-center gap-1.5 sm:gap-2 text-sm sm:text-base px-3 py-2 sm:px-4 sm:py-2.5"
                  style={{
                    background: 'linear-gradient(120deg, var(--purple) 0%, var(--pink) 50%, var(--orange) 100%)', backgroundSize: '200% 200%', animation: 'gradientShift 4s ease infinite',
                    color: 'white'
                  }}
                >
                  Dashboard
                </button>
              </>
            ) : (
              <>
                <Link
                  to="/login"
                  className={`px-3 py-2 sm:px-4 sm:py-2.5 ${textCls} ${hoverTextCls} transition-colors hidden sm:inline-flex items-center justify-center text-sm sm:text-base`}
                  style={showAnimations ? { animation: 'fadeInUp 0.8s ease-out 0.7s both' } : {}}
                >
                  {t('landing.nav.login')}
                </Link>
                <Link
                  to={registerHref}
                  className="bento-button hidden sm:inline-flex items-center justify-center gap-1.5 sm:gap-2 text-sm sm:text-base px-3 py-2 sm:px-4 sm:py-2.5"
                  onClick={() => handleRegisterClick('desktop_classic')}
                  style={{
                    background: 'linear-gradient(120deg, var(--purple) 0%, var(--pink) 50%, var(--orange) 100%)', backgroundSize: '200% 200%', animation: 'gradientShift 4s ease infinite',
                    animation: showAnimations ? 'fadeInUp 0.8s ease-out 0.9s both' : undefined,
                    color: 'white'
                  }}
                >
                  <LogIn className="h-3 w-3 sm:h-4 sm:w-4" />
                  Essai gratuit
                </Link>
              </>
            )}
          </div>
        </div>
      </div>

      {/* Menu mobile */}
      <div 
        className={`sm:hidden transition-all duration-300 ${mobileMenuOpen ? 'max-h-screen opacity-100' : 'max-h-0 opacity-0 overflow-hidden'}`} 
        style={{ backgroundColor: backgroundColor }}
      >
        <nav className="px-4 py-1">
          <Link
            to="/login"
            className={`block px-3 py-1.5 ${textCls} ${hoverBgCls} rounded transition-all duration-500 ${
              mobileMenuOpen ? 'opacity-100 translate-y-0' : 'opacity-0 -translate-y-2'
            }`}
            onClick={() => setMobileMenuOpen(false)}
            style={{ transitionDelay: mobileMenuOpen ? '0.1s' : '0s' }}
          >
            Se connecter
          </Link>
          <Link
            to="/brokers"
            className={`block px-3 py-1.5 ${textCls} ${hoverBgCls} rounded transition-all duration-500 ${
              mobileMenuOpen ? 'opacity-100 translate-y-0' : 'opacity-0 -translate-y-2'
            }`}
            onClick={() => setMobileMenuOpen(false)}
            style={{ transitionDelay: mobileMenuOpen ? '0.2s' : '0s' }}
          >
            Plateformes supportés
          </Link>
          <Link
            to="/help"
            className={`block px-3 py-1.5 ${textCls} ${hoverBgCls} rounded transition-all duration-500 ${
              mobileMenuOpen ? 'opacity-100 translate-y-0' : 'opacity-0 -translate-y-2'
            }`}
            onClick={() => setMobileMenuOpen(false)}
            style={{ transitionDelay: mobileMenuOpen ? '0.3s' : '0s' }}
          >
            FAQ
          </Link>
          <Link
            to="/pricing"
            className={`block px-3 py-1.5 ${textCls} ${hoverBgCls} rounded transition-all duration-500 ${
              mobileMenuOpen ? 'opacity-100 translate-y-0' : 'opacity-0 -translate-y-2'
            }`}
            onClick={() => setMobileMenuOpen(false)}
            style={{ transitionDelay: mobileMenuOpen ? '0.4s' : '0s' }}
          >
            Tarifs
          </Link>
          <Link
            to="/blog"
            className={`block px-3 py-1.5 ${textCls} ${hoverBgCls} rounded transition-all duration-500 ${
              mobileMenuOpen ? 'opacity-100 translate-y-0' : 'opacity-0 -translate-y-2'
            }`}
            onClick={() => setMobileMenuOpen(false)}
            style={{ transitionDelay: mobileMenuOpen ? '0.5s' : '0s' }}
          >
            Blog
          </Link>
          <Link
            to="/affiliate"
            className={`block px-3 py-1.5 ${textCls} ${hoverBgCls} rounded transition-all duration-500 ${
              mobileMenuOpen ? 'opacity-100 translate-y-0' : 'opacity-0 -translate-y-2'
            }`}
            onClick={() => setMobileMenuOpen(false)}
            style={{ transitionDelay: mobileMenuOpen ? '0.6s' : '0s' }}
          >
            Affiliation
          </Link>
          {user ? (
            <>
              <button
                onClick={() => {
                  navigate('/dashboard');
                  setMobileMenuOpen(false);
                }}
                className={`w-full text-left px-3 py-1.5 ${textCls} ${hoverBgCls} rounded transition-all duration-500 ${
                  mobileMenuOpen ? 'opacity-100 translate-y-0' : 'opacity-0 -translate-y-2'
                }`}
                style={{ transitionDelay: mobileMenuOpen ? '0.6s' : '0s' }}
              >
                Tableau de bord
              </button>
              <button
                onClick={() => {
                  handleLogout();
                  setMobileMenuOpen(false);
                }}
                className={`w-full text-left px-3 py-1.5 ${textCls} ${hoverBgCls} rounded transition-all duration-500 flex items-center gap-2 ${
                  mobileMenuOpen ? 'opacity-100 translate-y-0' : 'opacity-0 -translate-y-2'
                }`}
                style={{ transitionDelay: mobileMenuOpen ? '0.7s' : '0s' }}
              >
                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ color: iconColor }}>
                  <path d="m16 17 5-5-5-5"/>
                  <path d="M21 12H9"/>
                  <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"/>
                </svg>
                Se déconnecter
              </button>
            </>
          ) : (
            <Link
              to="/register"
              className={`block px-3 py-1.5 ${textCls} ${hoverBgCls} rounded transition-all duration-500 ${
                mobileMenuOpen ? 'opacity-100 translate-y-0' : 'opacity-0 -translate-y-2'
              }`}
              onClick={() => setMobileMenuOpen(false)}
              style={{ transitionDelay: mobileMenuOpen ? '0.6s' : '0s' }}
            >
              S'inscrire
            </Link>
          )}
        </nav>
      </div>
    </header>
  );
};

